import React from "react";
import axios from "axios";
import { Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/tennis`;
const SRC_CLS = { CONNECTED: "text-lime-300", BUILDING: "text-amber-300", MANUAL: "text-sky-300", "PENDING AUTHORIZATION": "text-amber-300", "DATA UNAVAILABLE": "text-amber-300", SKIPPED: "text-slate-500", EXCLUDED: "text-slate-500" };

export const GreenBallAudit = () => {
  const [d, setD] = React.useState(null);
  React.useEffect(() => { axios.get(`${API}/audit`).then((r) => setD(r.data)).catch(() => setD({ error: true })); }, []);
  if (!d) return <div className="flex items-center gap-2 py-8 font-mono text-xs text-slate-500"><Loader2 className="h-4 w-4 animate-spin text-lime-400" /> Grading results…</div>;
  if (d.error) return <p className="font-mono text-xs text-rose-400">Audit unavailable.</p>;
  const rows = Object.entries(d.records);
  return (
    <div className="space-y-3" data-testid="gb-audit">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {[["Graded picks", d.records.all.n], ["Hit %", d.records.all.rate != null ? `${d.records.all.rate}%` : "—"], ["Pending", d.pending], ["Total games MAE", d.totals_mae ?? "—"]].map(([l, v]) => (
          <div key={l} className="rounded-lg border border-slate-800 bg-slate-900/50 p-3" data-testid={`gb-audit-kpi-${l.replace(/\W+/g, "-").toLowerCase()}`}>
            <p className="font-mono text-[9px] uppercase tracking-widest text-slate-500">{l}</p><p className="font-display text-xl font-bold text-lime-200">{v}</p>
          </div>
        ))}
      </div>
      <div className="overflow-x-auto rounded-lg border border-slate-800">
        <table className="w-full font-mono text-[11px]">
          <thead className="bg-slate-900 text-[9px] uppercase text-slate-500"><tr><th className="px-2 py-1.5 text-left">Group</th><th className="px-2 py-1.5 text-right">W-L</th><th className="px-2 py-1.5 text-right">Hit %</th></tr></thead>
          <tbody>{rows.map(([k, r]) => <tr key={k} className="border-t border-slate-800/60" data-testid={`gb-audit-row-${k.replace(/\W+/g, "-").toLowerCase()}`}><td className="px-2 py-1.5 text-slate-300">{k.replace(":", " · ")}</td><td className="px-2 py-1.5 text-right">{r.w}-{r.l}</td><td className="px-2 py-1.5 text-right text-lime-200">{r.rate ?? "—"}</td></tr>)}</tbody>
        </table>
      </div>
      {d.calibration.length > 0 && (
        <div className="rounded-lg border border-slate-800 p-2 font-mono text-[10px] text-slate-400" data-testid="gb-calibration">
          Calibration: {d.calibration.map((c) => `${c.bin}-${c.bin + 9}%: predicted ${c.predicted} / actual ${c.actual} (${c.n})`).join(" · ")}
        </div>
      )}
      <div className="max-h-80 overflow-auto rounded-lg border border-slate-800">
        <table className="w-full font-mono text-[10px]" data-testid="gb-audit-recent">
          <thead className="sticky top-0 bg-slate-900 text-[9px] uppercase text-slate-500"><tr>{["Date", "Match", "Pick", "Prob", "Status", "Result", "Score"].map((h) => <th key={h} className="px-2 py-1.5 text-left">{h}</th>)}</tr></thead>
          <tbody>{d.recent.map((p, i) => (
            <tr key={`${p.date}-${i}`} className="border-t border-slate-800/60">
              <td className="px-2 py-1 text-slate-500">{p.date?.slice(5)}</td><td className="whitespace-nowrap px-2 py-1 text-slate-300">{p.players.join(" vs ")}</td>
              <td className="whitespace-nowrap px-2 py-1 text-slate-100">{p.pick}</td><td className="px-2 py-1">{Math.round(p.prob * 100)}%</td>
              <td className="px-2 py-1 text-slate-400">{p.status}</td>
              <td className={`px-2 py-1 font-bold ${p.result === "WIN" ? "text-lime-300" : p.result === "LOSS" ? "text-rose-400" : "text-slate-500"}`}>{p.result || "pending"}</td>
              <td className="px-2 py-1 text-slate-500">{p.score || ""}</td>
            </tr>))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const GreenBallSources = () => {
  const [d, setD] = React.useState(null);
  React.useEffect(() => { axios.get(`${API}/sources`).then((r) => setD(r.data)).catch(() => setD({ error: true })); }, []);
  if (!d) return <Loader2 className="h-4 w-4 animate-spin text-lime-400" />;
  if (d.error) return <p className="font-mono text-xs text-rose-400">Data sources unavailable.</p>;
  const Row = ({ s }) => (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-800 bg-slate-950/50 px-3 py-2" data-testid={`gb-source-${s.name.replace(/\W+/g, "-").toLowerCase()}`}>
      <div><p className="font-display text-[12px] font-bold uppercase text-slate-100">{s.name}</p><p className="font-mono text-[10px] text-slate-500">{s.covers || s.reason}</p></div>
      <span className={`font-mono text-[10px] font-bold ${SRC_CLS[s.status]}`}>{s.status}</span>
    </div>
  );
  return (
    <div className="space-y-3" data-testid="gb-sources">
      <p className="font-mono text-[10px] uppercase tracking-widest text-lime-400">Connected</p>
      {d.connected.map((s) => <Row key={s.name} s={s} />)}
      <p className="font-mono text-[10px] uppercase tracking-widest text-amber-400">Pending / unavailable</p>
      {d.pending.map((s) => <Row key={s.name} s={s} />)}
    </div>
  );
};
